'use strict';

const getVehiclePicker = () =>
	import(/* webpackChunkName: "get-vehicle-picker" */ 'public/js/components/vehicle-picker.js');

const getCart = () =>
	import(/* webpackChunkName: "get-cart" */ 'public/js/components/cart');

const ADD_TO_CART_FAILURE_REASON = {
	CART_MODULE_LOAD_FAILED: 'cart_module_load_failed',
	CART_UNAVAILABLE: 'cart_unavailable',
	MISSING_BRAND: 'missing_brand',
	MISSING_MPN: 'missing_mpn',
	MISSHAPEN_PAYLOAD: 'misshapen_payload',
};

const STEER_GA4_EVENT = {
	VEHICLE_PICKER_INITIALIZE: 'vehicle_picker_initialize',
};

const STEER_SOURCE = 'steer';

/** TODO: change once we have proper catalogs
 * Aftermarket tire products on lead-gen stores resolve via the custom catalog, not brand part source. */
const STEER_CATALOG = 'custom';

/**
 * @param {unknown} value
 * @returns {value is Record<string, unknown>}
 */
function isPlainObject(value) {
	return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * @param {unknown} detail
 * @returns {Record<string, unknown>}
 */
function normalizeSteerDetail(detail) {
	if (!isPlainObject(detail)) {
		return {};
	}

	return detail;
}

function openVehiclePickerFresh() {
	getVehiclePicker().then((module) => {
		module.getDataAndOpenVehiclePicker({ navigateAfterSelect: false });
	});
}

/**
 * @param {unknown} detail
 * @returns {{ ok: true, payload: { brand: string, mpn: string, quantity: number } } | { ok: false, reason: string }}
 */
function parseAddToCartPayload(detail) {
	if (!isPlainObject(detail)) {
		return { ok: false, reason: ADD_TO_CART_FAILURE_REASON.MISSHAPEN_PAYLOAD };
	}

	const brand = typeof detail.brand === 'string' ? detail.brand.trim() : '';
	const mpn = typeof detail.mpn === 'string' ? detail.mpn.trim() : '';

	if (brand === '') {
		return { ok: false, reason: ADD_TO_CART_FAILURE_REASON.MISSING_BRAND };
	}

	if (mpn === '') {
		return { ok: false, reason: ADD_TO_CART_FAILURE_REASON.MISSING_MPN };
	}

	const quantityValue = detail.quantity;
	const hasValidQuantity =
		typeof quantityValue === 'number'
		&& Number.isFinite(quantityValue)
		&& Number.isInteger(quantityValue)
		&& quantityValue > 0;
	const quantity = hasValidQuantity ? quantityValue : 1;

	return { ok: true, payload: { brand, mpn, quantity } };
}

/**
 * @param {{ brand: string, mpn: string, quantity: number }} payload
 * @returns {HTMLButtonElement}
 */
function buildAddToCartElement(payload) {
	const button = document.createElement('button');
	button.className = 'add-to-cart enabled';
	button.dataset.skuStripped = payload.mpn;
	button.dataset.catalog = STEER_CATALOG;
	button.dataset.brand = payload.brand;
	button.dataset.quantity = String(payload.quantity);
	button.dataset.cartSource = STEER_SOURCE;

	return button;
}

/**
 * @param {string} reason
 * @returns {string}
 */
function getSteerAddToCartFailureMessage(reason) {
	if (reason === ADD_TO_CART_FAILURE_REASON.MISSING_BRAND
		|| reason === ADD_TO_CART_FAILURE_REASON.MISSING_MPN
		|| reason === ADD_TO_CART_FAILURE_REASON.MISSHAPEN_PAYLOAD) {
		return 'Unable to add this item to your cart. Product details are incomplete.';
	}

	if (reason === ADD_TO_CART_FAILURE_REASON.CART_MODULE_LOAD_FAILED
		|| reason === ADD_TO_CART_FAILURE_REASON.CART_UNAVAILABLE) {
		return 'Unable to add this item to your cart right now. Please try again.';
	}

	const javascriptContent = globalThis.javascriptContent;
	if (javascriptContent && typeof javascriptContent.cart_item_add_error === 'string') {
		return javascriptContent.cart_item_add_error;
	}

	return 'Unable to add this item to your cart.';
}

/**
 * @param {string} message
 * @returns {void}
 */
function showSteerAddToCartFailureNotification(message) {
	const notify = globalThis.rp_app?.notify;
	if (typeof notify?.add_notification !== 'function') {
		return;
	}

	notify.add_notification({
		message,
		type: 'error',
	});
}

/**
 * @param {string} reason
 * @returns {void}
 */
function failSteerAddToCartEarly(reason) {
	showSteerAddToCartFailureNotification(getSteerAddToCartFailureMessage(reason));
}

/**
 * Clears body-level overlay spinners without relying on the overlaySpinner singleton ID.
 *
 * @returns {void}
 */
function removeSteerBodySpinners() {
	document.querySelectorAll('body > .spinner-wrap').forEach((spinnerElement) => {
		spinnerElement.remove();
	});
}

/**
 * @param {{ _cart_ele?: { hasClass: (className: string) => boolean }, _cart_ele_id?: string }} cart
 * @returns {boolean}
 */
function cartElementHasSaveFailed(cart) {
	const cartElement = cart._cart_ele;
	if (cartElement && typeof cartElement.hasClass === 'function') {
		return cartElement.hasClass('save_failed');
	}

	const rp$ = globalThis.rp$;
	if (typeof rp$ !== 'function') {
		return false;
	}

	const cartElementId = cart._cart_ele_id || 'rp_global_add_to_cart';
	const $cartElement = rp$('#' + cartElementId);
	if (typeof $cartElement.hasClass !== 'function') {
		return false;
	}

	return $cartElement.hasClass('save_failed');
}

/**
 * Shows the add-to-cart modal that cart.js already built during a failed save.
 *
 * @param {{ _cart_ele_id?: string }} cart
 * @returns {void}
 */
function showSteerCartConfirmationModal(cart) {
	const rp$ = globalThis.rp$;
	if (typeof rp$ !== 'function') {
		return;
	}

	const cartElementId = cart._cart_ele_id || 'rp_global_add_to_cart';
	rp$('#' + cartElementId)
		.show()
		.find('.modal')
		.modal({ show: true });
}

/**
 * @param {{ _cart_ele?: { hasClass: (className: string) => boolean }, _cart_ele_id?: string, open_cart: () => void }} cart
 * @returns {void}
 */
function completeSteerAddToCart(cart) {
	removeSteerBodySpinners();

	if (cartElementHasSaveFailed(cart)) {
		showSteerCartConfirmationModal(cart);
		return;
	}

	cart.open_cart();
}

/**
 * Same spinner + callback pattern as the delegated `.add-to-cart.enabled` click handler in cart.js.
 *
 * @param {import('public/js/components/cart')} cart
 * @param {HTMLButtonElement} button
 * @returns {void}
 */
function addSteerProductToCart(cart, button) {
	const rp$ = globalThis.rp$;
	if (typeof rp$ !== 'function') {
		return;
	}

	rp$('body').overlaySpinner();
	rp$('.spinner-overlay').css('position', 'fixed');
	rp$('.spinner-icon').css('position', 'fixed');

	cart.add_product_from_ele(button, {
		ignorePageQuantity: true,
		callback: function onSteerAddToCartComplete() {
			completeSteerAddToCart(cart);
		},
	});
}

/**
 * @param {Record<string, unknown>} context
 */
function handleVehiclePickerInitialize(context) {
	globalThis.rp_app.track.track_ec_general(STEER_GA4_EVENT.VEHICLE_PICKER_INITIALIZE, {
		...context,
		source: STEER_SOURCE,
	});
	openVehiclePickerFresh();
}

/**
 * @param {unknown} detail
 */
function handleAddToCart(detail) {
	const parseResult = parseAddToCartPayload(detail);
	if (!parseResult.ok) {
		failSteerAddToCartEarly(parseResult.reason);
		return;
	}

	getCart().then(() => {
		const cart = globalThis.rp_app?.cart;
		if (typeof cart?.add_product_from_ele !== 'function') {
			failSteerAddToCartEarly(ADD_TO_CART_FAILURE_REASON.CART_UNAVAILABLE);
			return;
		}

		const button = buildAddToCartElement(parseResult.payload);
		addSteerProductToCart(cart, button);
	}).catch(() => {
		failSteerAddToCartEarly(ADD_TO_CART_FAILURE_REASON.CART_MODULE_LOAD_FAILED);
	});
}

/** @type {Record<string, (context: Record<string, unknown>) => void>} */
const STEER_INBOUND_HANDLERS = {
	'add-to-cart': handleAddToCart,
	'vehicle-picker-initialize': handleVehiclePickerInitialize,
};

function registerSteerInboundListeners() {
	Object.entries(STEER_INBOUND_HANDLERS).forEach(([eventName, handler]) => {
		globalThis.addEventListener(eventName, (event) => {
			if (eventName === 'add-to-cart') {
				handler(event.detail);
				return;
			}

			handler(normalizeSteerDetail(event.detail));
		});
	});
}

module.exports = {
	ADD_TO_CART_FAILURE_REASON,
	STEER_GA4_EVENT,
	STEER_INBOUND_HANDLERS,
	STEER_CATALOG,
	STEER_SOURCE,
	addSteerProductToCart,
	buildAddToCartElement,
	cartElementHasSaveFailed,
	completeSteerAddToCart,
	getSteerAddToCartFailureMessage,
	handleAddToCart,
	handleVehiclePickerInitialize,
	normalizeSteerDetail,
	openVehiclePickerFresh,
	parseAddToCartPayload,
	registerSteerInboundListeners,
	removeSteerBodySpinners,
	showSteerCartConfirmationModal,
};
